import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { styled } from "styled-components";
import invariant from "ts-invariant";

import { useGetUserCourseQuery } from "api/hooks/useGetUserCourse";
import { useGetUserCourseTemplateQuery } from "api/hooks/useGetUserCourseTemplate";
import { usePostStartTreatment } from "api/hooks/usePostStartTreatment";
import { usePutUserCourseQuery } from "api/hooks/usePutUserCourse";
import type { FullUserCourse, HealthFormType, PutUserCourseArgs, PutUserCoursePayload } from "api/schemas/UserCourse";
import { useProfileContext } from "contexts/ProfileContext";
import { Title } from "routes/library/components/styles";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { Notification } from "shared/atoms/Notification";
import { usePageContentOffsetTop } from "shared/styles/usePageContentOffsetTop";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { CurrentPatientContext } from "utils/contexts";
import { useActivityLogging } from "utils/hooks/useActivityLogging";
import { isUsingActivityTracking } from "utils/patient";
import { userIsTherapist } from "utils/profile/profileHelper";

import { MarkTreatmentReadyConfirmation } from "./components/MarkTreatmentReadyConfirmation";
import { PatientDetails } from "./components/PatientDetails";
import { UserCourseEditor } from "./components/UserCourseEditor";
import { convertTemplateToUserCourse } from "./utils/conversion";
import { isSameCourse, removeUnchangedLevels2 } from "./utils/isSameCourse";
import { useHealthformWarning } from "./utils/useHealthformWarning";

export type UserCourseView = "exercises" | "lessons";

export const UserCourseScreen = () => {
  const [searchParams] = useSearchParams();
  const initialTemplateIdStr = searchParams.get("initialTemplateId");
  const initialTemplateId = initialTemplateIdStr !== null ? parseInt(initialTemplateIdStr, 10) : undefined;
  const initialShowExercisePicker = !!searchParams.get("showExercisePicker");

  const { profile } = useProfileContext();
  invariant(profile);
  const { patient, refreshPatient } = useContext(CurrentPatientContext);

  const patientId = patient.id;
  const currentProtocolWeek = patient.week;
  const isTherapist = userIsTherapist(profile);
  const startTime = useRef(new Date().toISOString());
  const [courseData, setCourseData] = useState<FullUserCourse | undefined>(undefined);

  const [showMarkTreatmentReadyConfirmation, setShowMarkTreatmentReadyConfirmation] = useState(false);
  const navigate = useNavigate();
  const { trackActivity } = useActivityLogging();
  const [exercisePickerVisible, setExercisePickerVisible] = useState(initialShowExercisePicker);
  const [hasDraftContent, setHasDraftContent] = useState(false);
  const { t } = useTranslation();
  const hasActivatedTreatment = !patient.awaiting_treatment_ready;
  const [showExitConfirm, setShowExitConfirm] = useState<"none" | "back" | "newcourse">("none");
  const offsetTop = usePageContentOffsetTop();
  const [healthFormType, setHealthFormType] = useState<HealthFormType | undefined>(undefined);
  const healthFormTypeMismatchWarning = useHealthformWarning({
    healthFormType,
    patient,
    hasActivatedTreatment,
  });

  const {
    data: savedCourseData,
    error: courseIsError,
    refetch: refetchSavedCourseData,
    isLoading: courseIsLoading,
  } = useGetUserCourseQuery(
    { patientId },
    {
      enabled: false,
      onSuccess: data => {
        if (!initialTemplateId) {
          setCourseData(data);
        }
      },
    }
  );

  const { mutateAsync: putCourseMutate, error: putCourseIsError } = usePutUserCourseQuery(
    { patientId },
    {
      onSuccess: () => {
        // Just in case patients comes from usercourse?initialTemplateId or showExercisePicker and refreshes page.
        navigate(`/patients/${patientId}/usercourse`, { replace: true });
      },
    }
  );

  const { error: getTemplateIsError, refetch: refetchTemplateData } = useGetUserCourseTemplateQuery(
    { templateId: initialTemplateId as number },
    {
      enabled: false,
      onSuccess: data => {
        setCourseData(convertTemplateToUserCourse(data));
      },
    }
  );

  const { mutateAsync: startTreatmentMutate, error: startTreatmentIsError } = usePostStartTreatment(
    { patientID: patientId },
    {
      onSuccess: () => {
        if (isTherapist && isUsingActivityTracking(patient)) {
          trackActivity({
            category: "treatment_created",
            patient_id: patientId,
            start_time: startTime.current,
            end_time: new Date().toISOString(),
          });
        }
        setShowMarkTreatmentReadyConfirmation(true);
        refreshPatient();
      },
    }
  );

  const saveTreatment = async (newCourse: PutUserCoursePayload) => {
    // BE issue workaround, don't change unchanged levels
    const trimmedExercises = removeUnchangedLevels2(courseData?.exercises || [], newCourse.exercises || []);

    const course: PutUserCourseArgs["course"] = {
      exercises: trimmedExercises,
      lessons: newCourse.lessons,
      max_exercises_per_day: newCourse.max_exercises_per_day,
      functional_test: newCourse.functional_test,
      health_form_type: newCourse.health_form_type,
    };

    await putCourseMutate({ patientId, course });
  };

  const isError = courseIsError || putCourseIsError || startTreatmentIsError || getTemplateIsError;

  // only load usercourse if not initated via templateId
  useEffect(() => {
    if (patientId) {
      refetchSavedCourseData();
    }
  }, [patientId, initialTemplateId]);

  useEffect(() => {
    if (initialTemplateId !== undefined) {
      refetchTemplateData();
    }
  }, [initialTemplateId]);

  const markTreatmentReady = async () => {
    AnalyticsService.track(AnalyticsEvents.USERCOURSE.ACTIVATE_COURSE, { patientId });
    await startTreatmentMutate({ patientID: patientId });
  };

  const onCourseChanged = useCallback(
    (course: PutUserCoursePayload) => {
      if (!courseIsLoading && savedCourseData && course) {
        const same = isSameCourse(savedCourseData, course);
        setHasDraftContent(!same);
      }
      setHealthFormType(course.health_form_type);
    },
    [courseData, courseIsLoading]
  );

  const goBack = () => navigate(-1);

  if (isError) {
    return (
      <div>
        <Notification variant="danger" style={{ marginBottom: "5px" }}>
          {t("errors.generic")}
        </Notification>
      </div>
    );
  }

  return (
    <>
      <Container>
        <TopContainer $offsetTop={offsetTop}>
          <TopContainerInner>
            <HeaderContainer>
              <>
                <span>
                  <TopLink
                    onClick={() => {
                      if (hasDraftContent) {
                        setShowExitConfirm("back");
                      } else {
                        setShowExitConfirm("none");
                        goBack();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {t("buttons.back")}
                  </TopLink>
                  <Title>
                    {patient.awaiting_treatment_ready ? t("usercourse.create_course") : t("usercourse.edit_course")}
                  </Title>
                </span>
                <PatientDetails ailment={patient.ailment} />
              </>
            </HeaderContainer>
          </TopContainerInner>
        </TopContainer>
        <BottomContainerOuter>
          <BottomContainerInner>
            <>
              {healthFormTypeMismatchWarning && <Warning>{healthFormTypeMismatchWarning}</Warning>}
              <UserCourseEditor
                exercisePickerVisible={exercisePickerVisible}
                onExercisePickerClosed={() => {
                  setExercisePickerVisible(false);
                }}
                showActiveSelector
                currentProtocolWeek={currentProtocolWeek}
                showNameInput={false}
                initialData={courseData}
                saveButtonLabel={t("buttons.save")}
                saveButtonDisabled={!hasDraftContent}
                onActivate={
                  !hasActivatedTreatment
                    ? async course => {
                        await saveTreatment(course);
                        await markTreatmentReady();
                      }
                    : undefined
                }
                canEditLessons={!hasActivatedTreatment}
                onSaveUserCourse={async course => {
                  await saveTreatment(course);
                  await refetchSavedCourseData();
                  // navigate(`/patients/${patientId}/protocol/current`);
                }}
                onCourseChanged={onCourseChanged}
                onNewCourse={() => {
                  if (hasDraftContent) {
                    setShowExitConfirm("newcourse");
                  } else {
                    setShowExitConfirm("none");
                    navigate(`/patients/${patientId}/usercourse/new`);
                  }
                }}
              />
              {showMarkTreatmentReadyConfirmation && (
                <MarkTreatmentReadyConfirmation
                  onClose={() => {
                    setShowMarkTreatmentReadyConfirmation(false);
                    navigate(`/patients/${patientId}/protocol/current`);
                  }}
                />
              )}
            </>
          </BottomContainerInner>
        </BottomContainerOuter>
      </Container>
      {showExitConfirm !== "none" && (
        <ConfirmationPopup
          isOpen
          title={t("usercourse.unsavedchanges.header")}
          body={t("usercourse.unsavedchanges.body")}
          onClickOutside={() => setShowExitConfirm("none")}
          onConfirm={() => {
            if (showExitConfirm === "newcourse") {
              navigate(`/patients/${patientId}/usercourse/new`);
            } else if (showExitConfirm === "back") {
              goBack();
            }
            setShowExitConfirm("none");
          }}
          onCancel={() => setShowExitConfirm("none")}
        />
      )}
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  margin: 0 auto;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.greys.light2};
`;

const TopContainer = styled.div<{ $offsetTop: number }>`
  background: ${({ theme }) => theme.colors.white};
  padding-top: ${props => props.$offsetTop}px;
`;

const TopContainerInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.white};
  flex-grow: 1;
  ${props => props.theme.aboveBreakpoint} {
    padding: 40px;
  }
`;

const BottomContainerOuter = styled.div`
  background: ${({ theme }) => theme.colors.greys.light2};
`;

const BottomContainerInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  ${props => props.theme.aboveBreakpoint} {
    padding: 40px;
  }
  ${props => props.theme.belowBreakpoint} {
    padding: 0 16px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  ${props => props.theme.aboveBreakpoint} {
    flex-direction: row;
  }
`;

const TopLink = styled.div`
  color: ${props => props.theme.colors.redesign.b100};
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  ${props => props.theme.belowBreakpoint} {
    justify-content: center;
  }
`;

const Warning = styled.div`
  background-color: ${props => props.theme.colors.salmon};
  margin-bottom: 32px;
  color: white;
  padding: 20px;
`;
